type GuestVerifyResponse = { token: string; expires_in: number };

type GuestDistribution = {
  id: string;
  team_id: string;
  club_id?: string | null;
  team_name?: string | null;
  coach_id?: string | null;
  created_by_coach_id?: string | null;
  requested_by_name?: string | null;
  requested_by_email?: string | null;
  name: string;
  evaluation_type_id: string;
  status: string;
};

type GuestInvitee = {
  email: string;
  first_name?: string;
  last_name?: string;
  status?: string;
};

type GuestMetaEvaluation = {
  id: string;
  name_translations?: Record<string, string>;
  description_translations?: Record<string, string>;
  criteria?: unknown;
};

type GuestPlayer = {
  id: string;
  name: string;
  number?: string | number;
  club_id?: string | null;
};

export type GuestSessionResponse = {
  evaluated_at?: string | null;
  distribution: GuestDistribution;
  invitee: GuestInvitee;
  meta_evaluation: GuestMetaEvaluation;
  players: GuestPlayer[];
  scores?: unknown;
};

export type GuestSubmitMode = 'draft' | 'submit';

export type GuestSubmitContext = {
  evaluatedAt?: string;
  evaluationId?: string | null;
  distributionId?: string | null;
  inviteeEmail?: string | null;
  teamId?: string | null;
  clubId?: string | null;
  evaluationTitle?: string | null;
  evaluatorEmail?: string | null;
};

export const GUEST_EVALUATION_TOKEN_KEY = 'guestEvaluationToken';
export const GUEST_EVALUATION_TOKEN_MAP_KEY = 'guestEvaluationTokenMap';

type StorageAdapter = Pick<Storage, 'getItem' | 'setItem'>;

export type GuestEvaluationsApiDependencies = {
  getApiBaseUrl: () => string;
  getApiKey?: () => string | undefined;
  getSessionStorage: () => StorageAdapter;
  fetch?: typeof globalThis.fetch;
};

const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const normalizeCode = (value: string): string => value.trim();
const tokenMapKeyFor = (email: string, code: string): string => `${normalizeEmail(email)}|${normalizeCode(code)}`;

export const createGuestEvaluationsApi = (dependencies: GuestEvaluationsApiDependencies) => {
  const fetchRequest = dependencies.fetch ?? globalThis.fetch;

  const getApiBaseUrl = (): string => {
    const raw = dependencies.getApiBaseUrl().trim();
    if (!raw) throw new Error('API URL ontbreekt (VITE_AI_API_URL)');
    return raw.replace(/\/$/, '');
  };

  const createHeaders = (token?: string): HeadersInit => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const apiKey = dependencies.getApiKey?.()?.trim();
    if (apiKey) headers['X-API-Key'] = apiKey;
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const parseErrorMessage = async (response: Response): Promise<string> => {
    try {
      const body = await response.json();
      if (typeof body?.message === 'string') return body.message;
      if (typeof body?.error === 'string') return body.error;
      if (typeof body?.detail === 'string') return body.detail;
      if (Array.isArray(body?.detail)) {
        const firstDetail = body.detail[0];
        if (typeof firstDetail?.msg === 'string' && Array.isArray(firstDetail?.loc)) {
          const rawField = firstDetail.loc[firstDetail.loc.length - 1];
          const field = typeof rawField === 'string' ? rawField : '';
          if (firstDetail.msg.toLowerCase() === 'field required') {
            if (field === 'email' || field === 'invitee_email' || field === 'evaluator_email') {
              return 'E-mailadres ontbreekt in de aanvraag.';
            }
            if (field === 'code' || field === 'access_code' || field === 'otp') {
              return 'Toegangscode ontbreekt in de aanvraag.';
            }
            return `Verplicht veld ontbreekt${field ? `: ${field}` : ''}.`;
          }
        }
        if (typeof firstDetail?.msg === 'string') return firstDetail.msg;
      }
    } catch {
      try {
        const text = await response.text();
        if (text.trim()) return text.trim();
      } catch {
        // Ignore invalid response bodies and use the status fallback.
      }
    }
    return `Request failed (${response.status})`;
  };

  const verifyGuestAccess = async (email: string, code: string): Promise<GuestVerifyResponse> => {
    const response = await fetchRequest(`${getApiBaseUrl()}/api/evaluations/guest/verify`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ email, code }),
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response));
    const payload = await response.json();
    const token =
      (typeof payload?.token === 'string' && payload.token)
      || (typeof payload?.guest_session_token === 'string' && payload.guest_session_token)
      || (typeof payload?.session_token === 'string' && payload.session_token)
      || '';
    if (!token) throw new Error('Geen sessietoken ontvangen van de server.');
    const expiresIn =
      typeof payload?.expires_in === 'number'
        ? payload.expires_in
        : typeof payload?.expiresIn === 'number'
          ? payload.expiresIn
          : 1800;
    return { token, expires_in: expiresIn };
  };

  const fetchGuestSession = async (token: string): Promise<GuestSessionResponse> => {
    const response = await fetchRequest(`${getApiBaseUrl()}/api/evaluations/guest/session`, {
      method: 'GET',
      headers: createHeaders(token),
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response));
    return response.json();
  };

  const submitGuestEvaluation = async (
    token: string,
    scores: unknown,
    mode: GuestSubmitMode = 'submit',
    context?: GuestSubmitContext,
  ): Promise<void> => {
    const isSubmit = mode === 'submit';
    const rawTitle = context?.evaluationTitle?.trim() || '';
    const rawEvaluatorEmail = context?.evaluatorEmail?.trim() || context?.inviteeEmail?.trim() || '';
    const clubId = context?.clubId?.trim() || undefined;
    const scoresPayload = typeof scores === 'object' && scores !== null && !Array.isArray(scores) ? scores : {};
    const response = await fetchRequest(`${getApiBaseUrl()}/api/evaluations/guest/submit`, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify({
        evaluated_at: context?.evaluatedAt,
        scores: scoresPayload,
        status: isSubmit ? 'completed' : 'draft',
        action: mode,
        submit: isSubmit,
        is_submit: isSubmit,
        finalize: isSubmit,
        evaluation_id: context?.evaluationId ?? undefined,
        teams_evaluation_id: context?.evaluationId ?? undefined,
        existing_evaluation_id: context?.evaluationId ?? undefined,
        distribution_id: context?.distributionId ?? undefined,
        invitee_email: context?.inviteeEmail ?? undefined,
        email: context?.inviteeEmail ?? undefined,
        team_id: context?.teamId ?? undefined,
        club_id: clubId,
        title: rawTitle || undefined,
        evaluation_title: rawTitle || undefined,
        evaluator_email: rawEvaluatorEmail || undefined,
      }),
    });
    if (!response.ok) throw new Error(await parseErrorMessage(response));
  };

  const readTokenMap = (): Record<string, string> => {
    try {
      const raw = dependencies.getSessionStorage().getItem(GUEST_EVALUATION_TOKEN_MAP_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, string>;
      }
    } catch {
      // Ignore invalid or unavailable session storage.
    }
    return {};
  };

  const writeTokenMap = (map: Record<string, string>): void => {
    dependencies.getSessionStorage().setItem(GUEST_EVALUATION_TOKEN_MAP_KEY, JSON.stringify(map));
  };

  const getStoredGuestTokenForCredentials = (email: string, code: string): string | null => {
    return readTokenMap()[tokenMapKeyFor(email, code)] || null;
  };

  const storeGuestTokenForCredentials = (email: string, code: string, token: string): void => {
    const map = readTokenMap();
    map[tokenMapKeyFor(email, code)] = token;
    writeTokenMap(map);
  };

  const clearStoredGuestTokenForCredentials = (email: string, code: string): void => {
    const map = readTokenMap();
    delete map[tokenMapKeyFor(email, code)];
    writeTokenMap(map);
  };

  return {
    clearStoredGuestTokenForCredentials,
    fetchGuestSession,
    getStoredGuestTokenForCredentials,
    storeGuestTokenForCredentials,
    submitGuestEvaluation,
    verifyGuestAccess,
  };
};
